<script setup>
import { ref, onMounted, watch } from 'vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'

const props = defineProps({
  groupId: { type: Number, required: true }
})

const { t } = useI18n()
const { fmtDate } = useFormatDate()

const loading = ref(false)
const error   = ref('')
const items   = ref([])
const total   = ref(0)

const columns = [
  { field: 'tactic',          header: t('hackwatch.ttps.colTactic'),     sortable: true  },
  { field: 'ttp_name',        header: t('hackwatch.ttps.colTechnique'),  sortable: true  },
  { field: 'mitre_id',        header: t('hackwatch.ttps.colMitre'),      sortable: false },
  { field: 'confidence',      header: t('hackwatch.ttps.colConfidence'), sortable: false },
  { field: 'link_created_at', header: t('hackwatch.ttps.colLinkedAt'),   sortable: true  },
]

function confidenceSeverity(val) {
  const v = String(val || '').toLowerCase()
  if (v === 'high') return 'danger'
  if (v === 'medium') return 'warning'
  if (v === 'low') return 'info'
  return 'secondary'
}

async function load(q = {}) {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.getGroupTTPs(props.groupId, {
      page:  q.page  ?? 1,
      limit: q.limit ?? 25,
    })
    items.value = Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : (data?.rows || []))
    total.value = typeof data?.total === 'number' ? data.total : items.value.length
  } catch (e) {
    console.error(e)
    error.value = t('hackwatch.ttps.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(() => load())
watch(() => props.groupId, () => load())

function onQueryChange(q) { load(q) }
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-sitemap hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendTtps') }}</span>
    </div>
    <AppTableWidget
      :columns="columns"
      :data="items"
      :total="total"
      :loading="loading"
      lazy
      :paginated="true"
      :searchable="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      data-key="ttp_id"
      :empty-text="t('hackwatch.groupDetail.noTtps')"
      @refresh="load()"
      @query-change="onQueryChange"
    >
      <template #cell-confidence="{ row }">
        <Tag :value="row.confidence || 'n/a'" :severity="confidenceSeverity(row.confidence)" />
      </template>
      <template #cell-link_created_at="{ row }">{{ fmtDate(row.link_created_at) }}</template>
    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>
